/**
 * Flag Library Service
 * High-performance flag lookup by flag_code with optimized MMSI prefix fallback
 *
 * Architecture:
 * - O(1) lookup by flag_code (authoritative from API)
 * - O(1) fallback lookup by MMSI prefix (when flag_code missing)
 * - Default flag for vessels without flag_code or valid MMSI
 * - Singleton pattern for performance
 */

import flagsData from '@/components/organisms/Vessels/utils/flags.json';

export interface FlagData {
  flag_code: string;
  flag_name: string;
  icon_png: string;
  mmsi: number[];
}

class FlagLibrary {
  private static instance: FlagLibrary;
  private flagsByCode: Map<string, FlagData> = new Map();
  private flagsByMMSIPrefix: Map<number, FlagData> = new Map();
  private defaultFlag: FlagData;

  private constructor() {
    const flags = flagsData as unknown as FlagData[];

    // Build index by flag_code (O(1) lookup)
    for (const flag of flags) {
      if (flag.flag_code) {
        this.flagsByCode.set(flag.flag_code.toUpperCase(), flag);
      }

      // Build index by MMSI prefix (O(1) fallback lookup)
      if (flag.mmsi && Array.isArray(flag.mmsi)) {
        for (const mmsiPrefix of flag.mmsi) {
          if (typeof mmsiPrefix === 'number' && mmsiPrefix > 0) {
            this.flagsByMMSIPrefix.set(mmsiPrefix, flag);
          }
        }
      }
    }

    // Set default flag (UNK - Unknown)
    this.defaultFlag = this.flagsByCode.get('UNK') || {
      flag_code: 'UNK',
      flag_name: 'Unknown',
      icon_png: '',
      mmsi: [],
    };
  }

  static getInstance(): FlagLibrary {
    if (!FlagLibrary.instance) {
      FlagLibrary.instance = new FlagLibrary();
    }
    return FlagLibrary.instance;
  }

  /**
   * Get flag by flag_code (O(1) lookup)
   * @param flagCode - Flag code from vessel data (e.g., "BAH", "PAN")
   * @returns Flag data or undefined if not found
   */
  getByCode(flagCode: string | undefined | null): FlagData | undefined {
    if (!flagCode) return undefined;
    return this.flagsByCode.get(flagCode.toUpperCase());
  }

  /**
   * Get flag by MMSI prefix (O(1) optimized lookup)
   * Validates MMSI format and extracts first 3 digits
   * @param mmsi - MMSI number or string
   * @returns Flag data or undefined if not found
   */
  getByMMSI(mmsi: string | number | undefined | null): FlagData | undefined {
    if (!mmsi) return undefined;

    // Convert to string and validate length
    const mmsiStr = mmsi.toString().trim();
    if (mmsiStr.length < 3) return undefined;

    // Extract first 3 digits (MMSI prefix)
    const prefix = parseInt(mmsiStr.substring(0, 3), 10);

    // Validate prefix is a valid number (100-999 range for MMSI prefixes)
    if (Number.isNaN(prefix) || prefix < 100 || prefix > 999) return undefined;

    return this.flagsByMMSIPrefix.get(prefix);
  }

  /**
   * Get flag by flag_code, or MMSI prefix fallback, or default flag
   * Optimized: tries flag_code first (most authoritative), then MMSI, then default
   * @param flagCode - Flag code from vessel data (optional)
   * @param mmsi - MMSI for fallback lookup (optional)
   * @returns Flag data (never undefined - returns default if needed)
   */
  getFlag(flagCode: string | undefined | null, mmsi?: string | number | undefined | null): FlagData {
    // Primary: Try flag_code (authoritative from API)
    if (flagCode) {
      const flag = this.getByCode(flagCode);
      if (flag) return flag;
    }

    // Fallback: Try MMSI prefix lookup (when flag_code missing)
    if (mmsi) {
      const flag = this.getByMMSI(mmsi);
      if (flag) return flag;
    }

    // Default: Return unknown flag
    return this.defaultFlag;
  }

  /**
   * Get the default/unknown flag
   * @returns Default flag data
   */
  getDefaultFlag(): FlagData {
    return this.defaultFlag;
  }

  /**
   * Get all flags (for debugging/admin purposes)
   */
  getAllFlags(): FlagData[] {
    return Array.from(this.flagsByCode.values());
  }

  /**
   * Get flag count
   */
  getFlagCount(): number {
    return this.flagsByCode.size;
  }
}

// Export singleton instance
export const flagLibrary = FlagLibrary.getInstance();
